import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { captureRefFromUrl } from "@/lib/referralStorage";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import HowItWorks from "@/components/HowItWorks";
import CookieConsentStrip from "@/components/CookieConsentStrip";

// Below-the-fold — lazy so landing JS chunk stays small.
const CategoriesSection = lazy(() => import("@/components/CategoriesSection"));
const LandingAvailablePrograms = lazy(() => import("@/components/programs/LandingAvailablePrograms"));
const Pricing = lazy(() => import("@/components/Pricing"));
const FAQPreview = lazy(() => import("@/components/FAQPreview"));
const FinalCTA = lazy(() => import("@/components/FinalCTA"));
const Footer = lazy(() => import("@/components/Footer"));
const AnnouncementStack = lazy(() => import("@/components/AnnouncementStack"));


const Index = () => {
  const location = useLocation();
  const { t } = useTranslation("landing");

  // Capture referral code from ?ref=CODE once per session
  useEffect(() => { captureRefFromUrl(); }, []);



  // Scroll to hash target on mount/hash change. Retries briefly because
  // sections like Pricing render after async data loads.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 20) setTimeout(tryScroll, 100);
    };
    // Defer to next tick so route transition / page swap settles first.
    const timer = setTimeout(tryScroll, 50);
    return () => clearTimeout(timer);
  }, [location.hash, location.key]);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "iAmerica",
    url: "https://iamerica.app",
    description: t("seo.home_description"),
    logo: "https://iamerica.app/iamerica-logo.svg",
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "iAmerica",
    url: "https://iamerica.app",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://iamerica.app/videos?search={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>{t("seo.home_title")}</title>
        <meta name="description" content={t("seo.home_description")} />
        <link rel="canonical" href="https://iamerica.app/" />
        <meta property="og:title" content={t("seo.home_title")} />
        <meta property="og:description" content={t("seo.home_description")} />
        <meta property="og:url" content="https://iamerica.app/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>
      <Navigation />
      <main className="flex-1">
        <Hero />
        <HowItWorks />
        <Suspense fallback={null}>
          <CategoriesSection />
          <LandingAvailablePrograms />
          <Pricing />
          <FAQPreview />
          <FinalCTA />
          <Footer />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <AnnouncementStack />
      </Suspense>
      <CookieConsentStrip />

    </div>
  );
};

export default Index;
